import { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { Icon, LatLngExpression, DivIcon } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPinIcon, LockClosedIcon, LockOpenIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import { ArrowPathIcon, MapIcon as MapIconSolid } from '@heroicons/react/24/solid';

// Fix for default marker icons in Leaflet
delete (Icon.Default.prototype as any)._getIconUrl;
Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom icon for user's current location - blue pulsing marker
const userLocationIcon = new DivIcon({
  className: 'custom-user-location-marker',
  html: `
    <div style="position: relative;">
      <div style="
        width: 20px;
        height: 20px;
        background-color: #3B82F6;
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        position: absolute;
        top: -10px;
        left: -10px;
        z-index: 1000;
      "></div>
      <div style="
        width: 20px;
        height: 20px;
        background-color: rgba(59, 130, 246, 0.3);
        border-radius: 50%;
        position: absolute;
        top: -10px;
        left: -10px;
        animation: pulse 2s infinite;
      "></div>
    </div>
    <style>
      @keyframes pulse {
        0% {
          transform: scale(1);
          opacity: 1;
        }
        50% {
          transform: scale(2);
          opacity: 0.5;
        }
        100% {
          transform: scale(2.5);
          opacity: 0;
        }
      }
    </style>
  `,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

// Standard radius options for all location filtering
export const LOCATION_RADIUS_OPTIONS = [
  { value: 10, label: '10m' },
  { value: 25, label: '25m' },
  { value: 100, label: '100m' },
  { value: 1000, label: '1km' },
  { value: 25000, label: '25km' },
  { value: 100000, label: '100km' },
];

export interface LocationData {
  latitude: number;
  longitude: number;
}

export interface ItemWithLocation {
  id: string;
  location?: LocationData;
  [key: string]: any;
}

interface LocationFilterMapProps<T extends ItemWithLocation> {
  items: T[];
  locationEnabled: boolean;
  currentLocation: LocationData | null;
  locationRadius: number;
  locationError: string;
  onEnableLocation: () => void;
  onDisableLocation: () => void;
  onRadiusChange: (radius: number) => void;
  renderMarkerPopup: (item: T, distance?: number) => React.ReactNode;
  onItemClick: (item: T) => void;
  className?: string;
  showEmbedded?: boolean; // For embedded map (e.g., in detail pages)
  centerOnLocation?: LocationData; // For embedded maps with specific center
}

// Calculate appropriate zoom level based on radius (limited to zoom 19 for map tile availability)
function getZoomForRadius(radiusInMeters: number): number {
  if (radiusInMeters <= 25) return 19;      // 10m-25m: Building level (closest zoom available)
  if (radiusInMeters <= 100) return 17;     // 100m: Block level
  if (radiusInMeters <= 1000) return 14;    // 1km: Town level
  if (radiusInMeters <= 25000) return 11;   // 25km: City level
  return 9;                                  // 100km: Regional level
}

// Component to recenter map when location changes (only if locked) and handle zoom
function RecenterMap({ center, isLocked, radius }: { center: LatLngExpression; isLocked: boolean; radius: number }) {
  const map = useMap();
  useEffect(() => {
    if (isLocked) {
      map.setView(center, map.getZoom());
    }
  }, [center, map, isLocked]);

  // Update zoom when radius changes
  useEffect(() => {
    const newZoom = getZoomForRadius(radius);
    map.setZoom(newZoom);
  }, [radius, map]);

  return null;
}

/**
 * Calculate distance between two GPS coordinates using Haversine formula
 */
export function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distance in meters
}

/**
 * Format distance for display
 */
export function formatDistance(distanceInMeters: number): string {
  if (distanceInMeters >= 1000) {
    return `${(distanceInMeters / 1000).toFixed(1)}km`;
  }
  return `${Math.round(distanceInMeters)}m`;
}

/**
 * Filter items by location and add distance
 */
export function filterItemsByLocation<T extends ItemWithLocation>(
  items: T[],
  currentLocation: LocationData | null,
  radius: number
): (T & { distance?: number })[] {
  if (!currentLocation) return items;

  return items
    .filter(item => {
      if (!item.location) return false;
      const distance = calculateDistance(
        currentLocation.latitude,
        currentLocation.longitude,
        item.location.latitude,
        item.location.longitude
      );
      return distance <= radius;
    })
    .map(item => ({
      ...item,
      distance: item.location
        ? calculateDistance(
            currentLocation.latitude,
            currentLocation.longitude,
            item.location.latitude,
            item.location.longitude
          )
        : undefined,
    }))
    .sort((a, b) => (a.distance || 0) - (b.distance || 0));
}

export function LocationFilterMap<T extends ItemWithLocation>({
  items,
  locationEnabled,
  currentLocation,
  locationRadius,
  locationError,
  onEnableLocation,
  onDisableLocation,
  onRadiusChange,
  renderMarkerPopup,
  onItemClick,
  className = '',
  showEmbedded = false,
  centerOnLocation,
}: LocationFilterMapProps<T>) {
  // Location lock state - when locked, map follows user location
  const [isLocationLocked, setIsLocationLocked] = useState(true);

  // Map layer state - street view or satellite imagery
  const [mapLayer, setMapLayer] = useState<'street' | 'satellite'>('street');

  // Calculate map center
  const mapCenter: LatLngExpression = centerOnLocation
    ? [centerOnLocation.latitude, centerOnLocation.longitude]
    : currentLocation
    ? [currentLocation.latitude, currentLocation.longitude]
    : [51.505, -0.09]; // Default to London

  // For embedded maps (detail pages), use fixed zoom; for full maps, use radius-based zoom
  const mapZoom = centerOnLocation ? 15 : getZoomForRadius(locationRadius);

  // Handle recenter button click
  const handleRecenter = () => {
    setIsLocationLocked(true);
  };

  // Toggle location lock
  const toggleLocationLock = () => {
    setIsLocationLocked(!isLocationLocked);
  };

  // Filter items with location data
  const itemsWithLocation = items.filter(item => item.location);

  // For embedded view, just show the map
  if (showEmbedded) {
    return (
      <div className={`overflow-hidden ${className}`} style={{ height: '300px', position: 'relative', zIndex: 10 }}>
        <MapContainer
          center={mapCenter}
          zoom={mapZoom}
          maxZoom={19}
          style={{ height: '100%', width: '100%', zIndex: 10 }}
        >
          {mapLayer === 'street' ? (
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              maxZoom={19}
            />
          ) : (
            <TileLayer
              attribution='Tiles &copy; Esri'
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              maxZoom={19}
            />
          )}

          {/* Center location marker (could be current location or specific location) */}
          {centerOnLocation && (
            <Marker
              position={[centerOnLocation.latitude, centerOnLocation.longitude]}
              icon={
                currentLocation &&
                centerOnLocation.latitude === currentLocation.latitude &&
                centerOnLocation.longitude === currentLocation.longitude
                  ? userLocationIcon
                  : undefined
              }
            >
              <Popup>
                {currentLocation &&
                centerOnLocation.latitude === currentLocation.latitude &&
                centerOnLocation.longitude === currentLocation.longitude
                  ? 'Your Location'
                  : 'Location'}
              </Popup>
            </Marker>
          )}

          {/* User location marker if different from center */}
          {currentLocation &&
            centerOnLocation &&
            (centerOnLocation.latitude !== currentLocation.latitude ||
              centerOnLocation.longitude !== currentLocation.longitude) && (
              <Marker
                position={[currentLocation.latitude, currentLocation.longitude]}
                icon={userLocationIcon}
              >
                <Popup>Your Location</Popup>
              </Marker>
            )}
        </MapContainer>
      </div>
    );
  }

  // Full view with filters and controls
  return (
    <div className={className}>
      {/* Location Filter Controls */}
      <div className="card mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <MapPinIcon className="w-5 h-5 text-gray-600" />
            <span className="text-sm font-medium text-gray-900">Filter by Location</span>
          </div>
          {!locationEnabled ? (
            <button
              onClick={onEnableLocation}
              className="text-sm btn btn-secondary py-1 px-3"
            >
              Enable Location
            </button>
          ) : (
            <button
              onClick={onDisableLocation}
              className="text-sm btn btn-secondary py-1 px-3"
            >
              Disable
            </button>
          )}
        </div>

        {locationError && (
          <div className="text-xs text-red-600 mb-2">{locationError}</div>
        )}

        {locationEnabled && currentLocation && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <button
                onClick={toggleLocationLock}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isLocationLocked
                    ? 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                title={isLocationLocked ? 'Map locked to your location' : 'Map unlocked - pan freely'}
              >
                {isLocationLocked ? (
                  <>
                    <LockClosedIcon className="w-4 h-4" />
                    <span>Locked</span>
                  </>
                ) : (
                  <>
                    <LockOpenIcon className="w-4 h-4" />
                    <span>Unlocked</span>
                  </>
                )}
              </button>
              {!isLocationLocked && (
                <button
                  onClick={handleRecenter}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                  title="Recenter on my location"
                >
                  <ArrowPathIcon className="w-4 h-4" />
                  <span>Recenter</span>
                </button>
              )}
              <button
                onClick={() => setMapLayer(mapLayer === 'street' ? 'satellite' : 'street')}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  mapLayer === 'satellite'
                    ? 'bg-green-100 text-green-800 hover:bg-green-200'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                title={mapLayer === 'street' ? 'Switch to satellite view' : 'Switch to street view'}
              >
                {mapLayer === 'satellite' ? (
                  <>
                    <GlobeAltIcon className="w-4 h-4" />
                    <span>Satellite</span>
                  </>
                ) : (
                  <>
                    <MapIconSolid className="w-4 h-4" />
                    <span>Street</span>
                  </>
                )}
              </button>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <label className="block text-xs text-gray-600 mb-1">
                  Radius: {formatDistance(locationRadius)} ({itemsWithLocation.length} nearby)
                </label>
                <select
                  value={locationRadius}
                  onChange={(e) => onRadiusChange(Number(e.target.value))}
                  className="input input-sm w-full text-sm"
                >
                  {LOCATION_RADIUS_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Map */}
      <div className="card p-0 overflow-hidden" style={{ height: '600px', position: 'relative', zIndex: 10 }}>
        <MapContainer
          center={mapCenter}
          zoom={mapZoom}
          maxZoom={19}
          style={{ height: '100%', width: '100%', zIndex: 10 }}
        >
          {mapLayer === 'street' ? (
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              maxZoom={19}
            />
          ) : (
            <TileLayer
              attribution='Tiles &copy; Esri'
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              maxZoom={19}
            />
          )}
          {currentLocation && <RecenterMap center={[currentLocation.latitude, currentLocation.longitude]} isLocked={isLocationLocked} radius={locationRadius} />}

          {/* User Location Marker */}
          {currentLocation && (
            <Marker
              position={[currentLocation.latitude, currentLocation.longitude]}
              icon={userLocationIcon}
            >
              <Popup>Your Location</Popup>
            </Marker>
          )}

          {/* Item Markers */}
          {itemsWithLocation.map(item => (
            <Marker
              key={item.id}
              position={[item.location!.latitude, item.location!.longitude]}
              eventHandlers={{
                click: () => onItemClick(item),
              }}
            >
              <Popup>{renderMarkerPopup(item, item.distance)}</Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
    </div>
  );
}
